import { useState } from 'react';

export const MixedUsePropertiesFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What qualifies as a mixed-use property?',
      answer: 'A mixed-use property combines two or more different uses in a single building, typically residential units with retail, office, or other commercial spaces. The most common configuration is ground-floor commercial with apartments above.'
    },
    {
      question: 'Do you buy properties with vacant commercial space?',
      answer: 'Yes, we frequently purchase mixed-use properties with vacant commercial spaces. We have the expertise to re-lease or reposition these spaces and factor the vacancy into our offer pricing.'
    },
    {
      question: 'How do you value mixed-use properties?',
      answer: 'We evaluate both the residential and commercial components separately, considering current income, market rents, tenant quality, lease terms, and property condition. We provide comprehensive valuations that account for the complexity of mixed-use properties.'
    },
    {
      question: 'What if commercial tenants have long-term leases?',
      answer: 'We work with existing lease structures and factor them into our valuation. Whether tenants have long-term leases at below-market rates or are month-to-month, we can structure an offer that works.'
    },
    {
      question: 'Can you handle complex zoning situations?',
      answer: 'Yes, we have extensive experience with zoning issues, use restrictions, and regulatory compliance for mixed-use properties. Our team includes professionals who specialize in navigating these complexities.'
    },
    {
      question: 'How long does it take to close on a mixed-use property?',
      answer: 'We typically close in 30-45 days, though mixed-use properties may require slightly more due diligence than purely residential properties. We work efficiently to minimize the timeline while conducting thorough analysis.'
    },
    {
      question: 'Do you buy properties that need major renovations?',
      answer: 'Yes, we specialize in properties needing significant capital investment. Whether it\'s building systems, facades, tenant improvements, or complete repositioning, we have the resources to handle major projects.'
    },
    {
      question: 'What happens to existing tenants?',
      answer: 'All existing leases transfer to us at closing. Both residential and commercial tenants remain in place under their current lease terms. We\'re experienced property owners who maintain good tenant relationships.'
    },
    {
      question: 'Do you charge broker commissions?',
      answer: 'No, we\'re the direct buyer so there are no broker commissions or fees. The offer we present is what you\'ll receive at closing, minus standard closing costs.'
    },
    {
      question: 'What markets do you buy mixed-use properties in?',
      answer: 'We purchase mixed-use properties in urban and suburban markets nationwide. We\'re particularly active in downtown areas, main streets, and transit-oriented locations, but we\'ll consider properties in any market.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Common questions about selling mixed-use properties to us.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-gray-50 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
                <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-teal-500 flex-shrink-0`}></i>
              </button>
              {openIndex === index && (
                <div className="px-8 pb-6">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">Still Have Questions?</h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            We're happy to answer any questions about selling your mixed-use property. Contact us for a confidential conversation.
          </p>
          <a 
            href="#get-offer" 
            className="whitespace-nowrap inline-block bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
          >
            Contact Us Today
          </a>
        </div>
      </div>
    </section>
  );
};